"use client";

/**
 * @file        useToolAccess.ts
 * @description Single source of truth for a gated tool's access state (quota +
 *              auth + antispam cooldown). Reads GET /api/tools/{toolId}/quota,
 *              normalises the response into a ToolAccessState discriminated by
 *              `status`, and re-reads canonical state on tab return
 *              (visibilitychange / focus) so an authorization completed in the
 *              Telegram bot tab is reflected without a page reload. Consumers
 *              (customs / encar / auction-sheet) render state via ToolAccessGate;
 *              /tools/calculator (lead-magnet) does NOT use this hook.
 * @rule        GET /quota is the source of truth for FULL state (telegramAuth,
 *              isLifetimeLimit, usedCount). A tool's own POST response gives only a
 *              fast optimistic `remaining` — feed it through reportFromResponse,
 *              which then schedules a refetch() to canonicalise (INV-E). Never
 *              treat a POST response as canonical state.
 * @rule        Cooldown (antispam: remaining > 0 but !allowed) and limit exhaustion
 *              (remaining === 0) are DISTINCT states (INV-B). cooldown → timer, no
 *              auth window; *_exhausted → auth / manager path, no timer.
 * @rule        Tab-return refetch is throttled to <=1 request / ~2s
 *              (visibilitychange + focus can both fire on a single return). Uses
 *              AbortController + window.setTimeout for canonicalisation — NEVER
 *              setInterval. Every fetch is abortable and aborted on unmount.
 * @rule        Fail-open: a network / parse error never hard-blocks the user —
 *              status becomes 'error' while remaining / maxCount keep their
 *              last-known values. The failure is NOT user-facing: `message` stays
 *              null (never the raw exception text) and ToolAccessGate renders
 *              nothing for 'error'. The reason is emitted once as a console.warn
 *              naming the tool, so the failure stays diagnosable in the browser
 *              without reaching the UI. An ABORTED fetch stays fully silent — no
 *              state change and no warning.
 * @universal   knowledge/universals/tools.md#usetoolaccess
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { MAX_ANONYMOUS_REQUESTS } from "@/lib/rateLimitConstants";

// ─── CONSTANTS ──────────────────────────────────────────────────────────────

/**
 * Daily request budget for a Telegram-authenticated user. Mirrors the authed
 * branch of src/lib/rateLimiter (10/day). Kept as a local literal because
 * rateLimiter.ts is Node-only (fs / crypto) and must not enter the client
 * bundle — the same boundary rateLimitConstants.ts exists to protect.
 */
const AUTH_DAILY_LIMIT = 10;

/** Fallback cooldown window (s) when the API omits an explicit resetIn. */
const DEFAULT_COOLDOWN_SECONDS = 120;

/** Min gap between tab-return refetches — visibilitychange + focus can double-fire. */
const REFETCH_THROTTLE_MS = 2000;

/** Delay before the post-optimistic canonicalising refetch (INV-E). */
const CANONICALISE_DELAY_MS = 500;

// ─── TYPES ────────────────────────────────────────────────────────────────

export type ToolId = "customs" | "auction" | "encar";

export type ToolAccessStatus =
  | "loading"
  | "ok"
  | "cooldown"
  | "anon_exhausted"
  | "auth_exhausted"
  | "queue_full"
  | "error";

/**
 * Access state for one gated tool. Discriminated by `status`; every numeric field
 * is present in every state so a consumer (and ToolAccessGate) can always show the
 * remaining-attempts counter (INV-C) regardless of which branch is active.
 *
 * 'queue_full' (server async-queue at capacity) is emitted by reportFromResponse on
 * a 503 POST response (auction async path). The GET /quota normaliser never emits it.
 */
export interface ToolAccessState {
  status: ToolAccessStatus;
  /** Attempts left in the current window. */
  remaining: number;
  /** Attempts already consumed in the current window. */
  usedCount: number;
  /** Window size: AUTH_DAILY_LIMIT when authenticated, else MAX_ANONYMOUS_REQUESTS. */
  maxCount: number;
  isAuthenticated: boolean;
  /** Seconds left on the antispam cooldown; 0 unless status === 'cooldown'. */
  cooldownSeconds: number;
  /** Optional human-readable note (errors / context); null when nothing to say. */
  message: string | null;
}

/** Shape consumed by the GET /quota normaliser. resetIn is optional (encar/auction). */
interface QuotaResponse {
  allowed?: boolean;
  remaining?: number;
  usedCount?: number;
  isLifetimeLimit?: boolean;
  telegramAuth?: boolean;
  resetIn?: number;
}

export interface UseToolAccess {
  state: ToolAccessState;
  /** GET /api/tools/{toolId}/quota → normalise into ToolAccessState (canonical). */
  refetch: () => void;
  /** Optimistic update from a tool's own POST response, then canonicalise (INV-E). */
  reportFromResponse: (resp: {
    status?: number;
    remaining?: number;
    isLifetimeLimit?: boolean;
    resetIn?: number;
  }) => void;
}

/** toolId → URL path segment. 'auction' is served under /auction-sheet. */
const TOOL_PATH: Record<ToolId, string> = {
  customs: "customs",
  auction: "auction-sheet",
  encar: "encar",
};

// ─── PURE NORMALISERS ───────────────────────────────────────────────────────

function statusFromQuota(
  allowed: boolean,
  remaining: number,
  isLifetimeLimit: boolean,
): ToolAccessStatus {
  if (remaining > 0) return allowed ? "ok" : "cooldown";
  return isLifetimeLimit ? "anon_exhausted" : "auth_exhausted";
}

function normaliseQuota(data: QuotaResponse): ToolAccessState {
  const telegramAuth = data.telegramAuth ?? false;
  const remaining = Math.max(0, data.remaining ?? 0);
  const isLifetimeLimit = data.isLifetimeLimit ?? false;
  const allowed = data.allowed ?? remaining > 0;
  const maxCount = telegramAuth ? AUTH_DAILY_LIMIT : MAX_ANONYMOUS_REQUESTS;
  const status = statusFromQuota(allowed, remaining, isLifetimeLimit);
  return {
    status,
    remaining,
    usedCount: data.usedCount ?? Math.max(0, maxCount - remaining),
    maxCount,
    isAuthenticated: telegramAuth,
    cooldownSeconds:
      status === "cooldown" ? data.resetIn ?? DEFAULT_COOLDOWN_SECONDS : 0,
    message: null,
  };
}

const INITIAL_STATE: ToolAccessState = {
  status: "loading",
  remaining: MAX_ANONYMOUS_REQUESTS,
  usedCount: 0,
  maxCount: MAX_ANONYMOUS_REQUESTS,
  isAuthenticated: false,
  cooldownSeconds: 0,
  message: null,
};

// ─── HOOK ─────────────────────────────────────────────────────────────────

export function useToolAccess(toolId: ToolId): UseToolAccess {
  const [state, setState] = useState<ToolAccessState>(INITIAL_STATE);

  const fetchAbortRef = useRef<AbortController | null>(null);
  const canonTimeoutRef = useRef<number | null>(null);
  const lastFetchAtRef = useRef<number>(0);
  const mountedRef = useRef<boolean>(true);

  const refetch = useCallback(() => {
    lastFetchAtRef.current = Date.now();
    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    void (async () => {
      try {
        const res = await fetch(`/api/tools/${TOOL_PATH[toolId]}/quota`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: QuotaResponse = await res.json();
        if (!mountedRef.current) return;
        setState(normaliseQuota(data));
      } catch (err) {
        if (controller.signal.aborted || !mountedRef.current) return;
        const reason = err instanceof Error ? err.message : "quota_unreachable";
        // Developer-only signal: the raw exception text must never reach the UI.
        console.warn(`[useToolAccess] quota read failed for "${toolId}": ${reason}`);
        // Fail-open: keep last-known numbers, just flag the read failure. `message`
        // stays null — the gate renders nothing for 'error'.
        setState((prev) => ({
          ...prev,
          status: "error",
          message: null,
        }));
      }
    })();
  }, [toolId]);

  const reportFromResponse = useCallback<UseToolAccess["reportFromResponse"]>(
    (resp) => {
      setState((prev) => {
        if (resp.status === 503) {
          // Server async-queue at capacity — a queue state, NOT a quota limit.
          // Keep remaining/usedCount/maxCount from prev so the counter survives
          // (INV-C). ToolAccessGate renders the queue_full branch.
          return { ...prev, status: "queue_full", cooldownSeconds: 0, message: null };
        }
        if (resp.status === 429) {
          // Cooldown (timer) vs hard exhaustion (auth/manager) — INV-B.
          if (resp.resetIn && resp.resetIn > 0) {
            return {
              ...prev,
              status: "cooldown",
              remaining: resp.remaining ?? prev.remaining,
              cooldownSeconds: resp.resetIn,
              message: null,
            };
          }
          const lifetime = resp.isLifetimeLimit ?? !prev.isAuthenticated;
          return {
            ...prev,
            status: lifetime ? "anon_exhausted" : "auth_exhausted",
            remaining: 0,
            usedCount: prev.maxCount,
            cooldownSeconds: 0,
            message: null,
          };
        }
        // 200 / unknown: optimistic remaining update.
        const remaining = Math.max(0, resp.remaining ?? prev.remaining);
        const status: ToolAccessStatus =
          remaining > 0
            ? "ok"
            : prev.isAuthenticated
              ? "auth_exhausted"
              : "anon_exhausted";
        return {
          ...prev,
          status,
          remaining,
          usedCount: Math.max(0, prev.maxCount - remaining),
          cooldownSeconds: 0,
          message: null,
        };
      });

      // Canonicalise against GET /quota (INV-E) — full state incl. telegramAuth.
      if (canonTimeoutRef.current !== null) {
        window.clearTimeout(canonTimeoutRef.current);
      }
      canonTimeoutRef.current = window.setTimeout(() => {
        canonTimeoutRef.current = null;
        refetch();
      }, CANONICALISE_DELAY_MS);
    },
    [refetch],
  );

  // Mount: initial read + tab-return listeners (INV-A).
  useEffect(() => {
    mountedRef.current = true;
    refetch();

    const maybeRefetch = () => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState !== "visible"
      ) {
        return;
      }
      if (Date.now() - lastFetchAtRef.current < REFETCH_THROTTLE_MS) return;
      refetch();
    };

    document.addEventListener("visibilitychange", maybeRefetch);
    window.addEventListener("focus", maybeRefetch);

    return () => {
      mountedRef.current = false;
      document.removeEventListener("visibilitychange", maybeRefetch);
      window.removeEventListener("focus", maybeRefetch);
      fetchAbortRef.current?.abort();
      if (canonTimeoutRef.current !== null) {
        window.clearTimeout(canonTimeoutRef.current);
        canonTimeoutRef.current = null;
      }
    };
  }, [refetch]);

  return { state, refetch, reportFromResponse };
}
